import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { KpiCard } from '@/components/ui/KpiCard'
import { DashboardService, type AdminDashboard } from '@/services/modules/DashboardService'
import { brlFromCents } from '@/lib/format'

const TYPE_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  FAMILIAR: 'Familiar',
  EMPRESARIAL: 'Empresarial',
}

export function DashboardPage({ title = 'Painel', subtitle = 'Visão geral do sistema Ampla Mais.' }: {
  title?: string
  subtitle?: string
} = {}) {
  const [data, setData] = useState<AdminDashboard | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    DashboardService.admin().then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>

      {loading || !data ? (
        <div className="text-sm text-slate-500">{loading ? 'Carregando' : 'Não foi possível carregar o painel.'}</div>
      ) : (
        <>
          {/* 01 — Faturamento pago dos últimos 3 anos */}
          <Section title="Faturamento pago" subtitle="Total recebido nos últimos 3 anos">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {data.yearlyPaid.map((y) => (
                <KpiCard
                  key={y.year}
                  label={String(y.year)}
                  value={brlFromCents(y.total)}
                  hint={y.growthPct === null ? undefined : <GrowthBadge pct={y.growthPct} />}
                />
              ))}
            </div>
          </Section>

          {/* 02 — Mês corrente: pendentes, pagos e comissões */}
          <Section title="Mês corrente" subtitle="Faturas e comissões do mês atual">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label="Pendente"
                accent="amber"
                value={brlFromCents(data.currentMonth.pending.total)}
                hint={`${data.currentMonth.pending.count} fatura(s)`}
              />
              <KpiCard
                label="Pago"
                accent="green"
                value={brlFromCents(data.currentMonth.paid.total)}
                hint={`${data.currentMonth.paid.count} fatura(s)`}
              />
              <KpiCard
                label="Comissões pagas"
                value={data.currentMonth.commissions.total.count}
                hint={brlFromCents(data.currentMonth.commissions.total.amount)}
              />
              <KpiCard
                label="Direto / Rede"
                value={`${data.currentMonth.commissions.firstLevel.count} / ${data.currentMonth.commissions.network.count}`}
                hint="1º nível da árvore / via rede"
              />
            </div>
          </Section>

          {/* 03 — Gráficos: adesões + meios de pagamento */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-1 font-medium text-slate-900">Adesões mensais</h3>
              <p className="mb-4 text-xs text-slate-500">Titulares (individual ou empresa) — últimos 6 meses</p>
              <LineChart data={data.monthlyAdhesions.map((m) => ({ label: m.label, value: m.count }))} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-1 font-medium text-slate-900">Meios de pagamento</h3>
              <p className="mb-4 text-xs text-slate-500">Associados individuais + empresas ativas</p>
              <BarChart data={data.paymentMethods.map((p) => ({ label: p.method, value: p.count }))} />
            </div>
          </div>

          {/* 04 — Assinaturas ativas por tipo */}
          <Section title="Assinaturas ativas" subtitle={`${data.activeSubscriptions.total} no total`}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard label="Total ativas" accent="brand" value={data.activeSubscriptions.total} />
              {data.activeSubscriptions.byType.map((t) => (
                <KpiCard key={t.type} label={TYPE_LABELS[t.type] ?? t.type} value={t.total} />
              ))}
            </div>
          </Section>

          {/* 05 + 06 — Últimas empresas e assinaturas */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-medium text-slate-900">Últimas empresas</h3>
                <Link to="/commercial/companies" className="text-xs font-medium text-brand-600 hover:underline">Ver todas</Link>
              </div>
              <RecentList
                rows={data.latestCompanies.map((c) => ({
                  id: c.id,
                  to: `/commercial/companies/${c.id}/editar`,
                  primary: c.name,
                  secondary: c.cpfCnpj ?? '—',
                  meta: c.formatted.createdAt ?? '',
                  status: c.status,
                }))}
                empty="Nenhuma empresa cadastrada."
              />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-medium text-slate-900">Últimas assinaturas</h3>
                <Link to="/commercial/subscribers" className="text-xs font-medium text-brand-600 hover:underline">Ver todas</Link>
              </div>
              <RecentList
                rows={data.latestSubscribers.map((s) => ({
                  id: s.id,
                  to: `/commercial/subscribers/${s.id}/editar`,
                  primary: s.name,
                  secondary: s.planName ?? '—',
                  meta: s.formatted.createdAt ?? '',
                  status: s.status,
                }))}
                empty="Nenhuma assinatura cadastrada."
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
        {subtitle ? <p className="text-xs text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  )
}

export function GrowthBadge({ pct }: { pct: number }) {
  const positive = pct >= 0
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${positive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
      {positive ? '▲' : '▼'} {Math.abs(pct).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
      <span className="font-normal text-slate-400">vs. ano anterior</span>
    </span>
  )
}

function BarChart({ data }: { data: { label: string; value: number }[] }) {
  if (data.length === 0) return <p className="text-sm text-slate-400">Sem dados.</p>
  const max = Math.max(...data.map((d) => d.value), 1)
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="w-32 break-words text-xs text-slate-600" title={d.label}>{d.label}</span>
          <div className="h-5 flex-1 overflow-hidden rounded bg-slate-100">
            <div className="h-full rounded bg-brand-500" style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right text-xs font-medium text-slate-700">{d.value}</span>
        </div>
      ))}
    </div>
  )
}

export function LineChart({ data }: { data: { label: string; value: number }[] }) {
  if (data.length === 0) return <p className="text-sm text-slate-400">Sem dados.</p>
  const w = 480
  const h = 160
  const pad = { top: 16, right: 12, bottom: 28, left: 32 }
  const innerW = w - pad.left - pad.right
  const innerH = h - pad.top - pad.bottom
  const max = Math.max(...data.map((d) => d.value), 1)
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0
  const points = data.map((d, i) => {
    const x = pad.left + i * stepX
    const y = pad.top + innerH - (d.value / max) * innerH
    return { x, y, ...d }
  })
  const line = points.map((p) => `${p.x},${p.y}`).join(' ')
  const area = `${pad.left},${pad.top + innerH} ${line} ${pad.left + (data.length - 1) * stepX},${pad.top + innerH}`

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" role="img" aria-label="Adesões mensais">
      {[0, 0.5, 1].map((t) => {
        const y = pad.top + innerH - t * innerH
        return <line key={t} x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f1f5f9" strokeWidth={1} />
      })}
      <polygon points={area} fill="var(--color-brand-500)" fillOpacity={0.1} />
      <polyline points={line} fill="none" stroke="var(--color-brand-500)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p) => (
        <g key={p.label}>
          <circle cx={p.x} cy={p.y} r={3.5} fill="#fff" stroke="var(--color-brand-500)" strokeWidth={2} />
          <text x={p.x} y={p.y - 8} textAnchor="middle" className="fill-slate-700" fontSize={10} fontWeight={600}>{p.value}</text>
          <text x={p.x} y={h - 8} textAnchor="middle" className="fill-slate-400" fontSize={10}>{p.label}</text>
        </g>
      ))}
    </svg>
  )
}

export function RecentList({ rows, empty }: { rows: { id: string; to?: string | null; primary: string; secondary: string; meta: string; status: string }[]; empty: string }) {
  if (rows.length === 0) return <p className="text-sm text-slate-400">{empty}</p>
  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((r) => {
        const inner = (
          <>
            <div className="min-w-0">
              <p className="break-words text-sm font-medium text-slate-900">{r.primary}</p>
              <p className="break-words text-xs text-slate-500">{r.secondary}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2 text-right">
              <span className="text-xs text-slate-400">{r.meta}</span>
              <span
                className={`inline-block h-2 w-2 rounded-full ${r.status === 'ATIVO' ? 'bg-green-500' : 'bg-slate-300'}`}
                title={r.status === 'ATIVO' ? 'Ativo' : 'Inativo'}
              />
            </div>
          </>
        )
        return (
          <li key={r.id}>
            {r.to
              ? <Link to={r.to} className="flex items-center justify-between gap-3 py-2.5 transition hover:bg-slate-50">{inner}</Link>
              : <div className="flex items-center justify-between gap-3 py-2.5">{inner}</div>}
          </li>
        )
      })}
    </ul>
  )
}

export default DashboardPage
